import React, { useState, useRef, useEffect, useLayoutEffect, useCallback } from 'react';
import { Send, User, Bot, Brain, X, AlertCircle, MessageCircle, Loader2, FileText, Code } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { webSocketClient, handleStreamingResponse, cancelChatMessage, YAMLFix, CloudFix, ThinkingStep } from '@/api/websocket';
import { useAuth } from '@/hooks/useAuth';
import { useChatSessionManager } from '@/hooks/useChatSessionManager';
import { ThinkingDisplay } from '@/components/chat/ThinkingStepItem';
import { ChatMarkdown } from '@/components/chat/MarkdownRenderer';
import { ChatHistoryDropdown } from '@/components/chat/ChatHistoryDropdown';
import { ChatMessage } from '@/components/chat/chat-utils';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { apiClient } from '@/api/client';
import { Link } from 'react-router-dom';

export interface BaseChatConfig {
  /** Function to send the message via WebSocket, returns message ID */
  sendMessage: (inputText: string) => string;
  /** Sample prompts for empty state */
  samplePrompts: string[];
  /** Header icon component */
  headerIcon: React.ReactNode;
  /** Header title */
  headerTitle: string;
  /** Empty state heading */
  emptyHeading: string;
  /** Empty state description */
  emptyDescription: string;
  /** Placeholder for text input */
  inputPlaceholder: string;
  /** Color theme: 'primary' (blue) or 'amber' */
  colorTheme: 'primary' | 'amber';
  /** Which fix type this chat receives */
  fixType: 'yaml' | 'cloud';
  /** Render function for fixes in a completed message */
  renderFixes?: (message: ChatMessage) => React.ReactNode;
  /** Render function for fixes during streaming */
  renderStreamingFixes?: (fixes: YAMLFix[] | CloudFix[]) => React.ReactNode;
  /** Loading message for fixes */
  fixesLoadingText?: string;
  /** Optional context for chat history (e.g., incident_id, finding_id) */
  contextType?: string;
  contextId?: string;
  /** Hide the large icon in the empty state */
  hideEmptyIcon?: boolean;
  /** Render extra content to the left of the input area (e.g., a "+" button) */
  renderInputPrefix?: () => React.ReactNode;
  /** Render extra buttons in the header row (e.g., close button) */
  renderHeaderExtra?: () => React.ReactNode;
  /** Render content above the input (e.g., attached file chips) */
  renderInputAbove?: () => React.ReactNode;
  /** Hide the disclaimer text below the input */
  hideDisclaimer?: boolean;
  /** Hide the title header bar entirely (ChatGPT-style full-page chat).
      History/new-chat controls float over the top-right corner instead. */
  hideHeader?: boolean;
  /** Callback when user saves a workflow suggestion from chat */
  onWorkflowSave?: (workflow: any) => Promise<boolean | void> | boolean | void;
  /** Callback when user creates a draft workflow from chat */
  onWorkflowCreate?: (workflow: any) => void;
  /** Callback when user wants to edit the workflow in the builder */
  onWorkflowEdit?: (workflow: any) => void;
  /** Auto-invoked with the parsed workflow when a completed bot message
   *  contains a workflow block (builder mode: apply directly to the canvas). */
  onWorkflowGenerated?: (workflow: any) => void;
  /** Builder mode: render completed workflow blocks as a compact
   *  "applied to canvas" chip instead of the full graph card, since the
   *  canvas beside the chat already shows the workflow. */
  compactWorkflowBlocks?: boolean;
  /** Hide the chat-history and new-chat controls: builder mode keeps exactly
   *  one session per workflow so its creation history stays coherent. */
  hideSessionControls?: boolean;
  /** Show the platform's amber Simulated badge: the conversation runs with
   *  every integration treated as connected and emits sandboxed workflows. */
  simulatedBadge?: boolean;
  /** ChatGPT semantics: open on a blank chat (don't auto-load the latest
   *  session) and create the session lazily on first send. */
  startBlankSession?: boolean;
  /** Which product subscription to check ('copilot' or 'workflows'). Defaults to 'copilot'. */
  requiredProduct?: 'copilot' | 'workflows';
  /** Returns current attachments to display on user messages (files, repos, etc.) */
  getAttachments?: () => Array<{ type: string; name: string }>;
}

function AttachmentIcon({ type }: { type: string }) {
  const cls = "h-3 w-3 shrink-0";
  switch (type) {
    case 'github': return <img src="https://github.githubassets.com/favicons/favicon-dark.svg" alt="" className={cls} />;
    case 'gitlab': return <img src="/assets/icons/gitlab-clean.svg" alt="" className={cls} />;
    case 'confluence': return <img src="/assets/icons/confluence.svg" alt="" className={cls} />;
    case 'notion': return <img src="/assets/icons/notion-logo-white.png" alt="" className={cls} />;
    case 'glean': return <img src="/assets/icons/glean.svg" alt="" className={cls} />;
    case 'script': return <Code className={cls} />;
    default: return <FileText className={cls} />;
  }
}

export function BaseChat({ config }: { config: BaseChatConfig }) {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [connectionError, setConnectionError] = useState<string | null>(null);
  const [isTyping, setIsTyping] = useState(false);
  const [isThinking, setIsThinking] = useState(false);
  const [thinkingText, setThinkingText] = useState('');
  const [thinkingSteps, setThinkingSteps] = useState<ThinkingStep[]>([]);
  const [thinkingComplete, setThinkingComplete] = useState(false);
  const [isThinkingExpanded, setIsThinkingExpanded] = useState(true);
  const [currentBotMessage, setCurrentBotMessage] = useState('');
  const [currentFixes, setCurrentFixes] = useState<YAMLFix[] | CloudFix[] | null>(null);
  const [waitingForFixes, setWaitingForFixes] = useState(false);
  const [canCancel, setCanCancel] = useState(false);
  const [currentMessageId, setCurrentMessageId] = useState<string | null>(null);
  const [copilotBlocked, setCopilotBlocked] = useState(false);

  const scrollAreaRef = useRef<HTMLDivElement>(null);

  // Full-page (Home) empty state on mobile: compact the heading/prompts when
  // the full-size block genuinely overflows the chat container. Fixed
  // viewport-height thresholds cannot express this — "fits" depends on the
  // browser chrome (a real iPhone 14's Safari reports ~660px and overflows,
  // the same page in devtools reports 844px and fits), so we measure the
  // scroll container directly. One-way latch against feedback loops: the
  // full-size content height is recorded while un-compacted, and compact
  // mode only reverts when that remembered height would fit again.
  const [compactEmpty, setCompactEmpty] = useState(false);
  const fullEmptyHeightRef = useRef<number | null>(null);
  const emptyStateVisible = messages.length === 0 && !isLoading && !connectionError;
  useLayoutEffect(() => {
    if (!config.hideHeader || !emptyStateVisible) return;
    const el = scrollAreaRef.current;
    if (!el) return;
    const evaluate = () => {
      // Web view is out of scope: only compact on mobile widths.
      if (!window.matchMedia('(max-width: 639px)').matches) {
        setCompactEmpty(false);
        return;
      }
      setCompactEmpty(prev => {
        if (!prev) {
          fullEmptyHeightRef.current = el.scrollHeight;
          return el.scrollHeight > el.clientHeight + 1;
        }
        const fullHeight = fullEmptyHeightRef.current;
        return fullHeight == null || fullHeight > el.clientHeight;
      });
    };
    evaluate();
    const ro = new ResizeObserver(evaluate);
    ro.observe(el);
    window.addEventListener('resize', evaluate);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', evaluate);
    };
  }, [config.hideHeader, emptyStateVisible]);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const sendGuardRef = useRef(false);

  // Reset send guard when loading finishes
  useEffect(() => {
    if (!isLoading) sendGuardRef.current = false;
  }, [isLoading]);

  const waitingForFixesTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const timeoutTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const responseHandlerRef = useRef<((msg: any) => void) | null>(null);

  const { isAuthenticated } = useAuth();

  // Check product subscription access
  useEffect(() => {
    apiClient.getBillingSubscriptions()
      .then((subs: any[]) => {
        if (!subs || subs.length === 0) return; // Legacy customer — no restriction
        const productKey = config.requiredProduct || 'copilot';
        const sub = subs.find((s: any) => s.product === productKey);
        if (!sub) {
          setCopilotBlocked(true);
        } else if (sub.status === 'trial' && sub.trial_ends_at && new Date(sub.trial_ends_at) < new Date()) {
          setCopilotBlocked(true);
        }
      })
      .catch(() => {}); // On error, don't block
  }, []);

  // Chat history (only active when context is provided)
  const sessionManager = useChatSessionManager(
    { contextType: config.contextType, contextId: config.contextId, startBlank: config.startBlankSession },
    setMessages
  );

  // Ref to avoid stale closure in handleSessionNotification
  const currentSessionIdRef = useRef(sessionManager.chatHistory.currentSessionId);
  currentSessionIdRef.current = sessionManager.chatHistory.currentSessionId;

  // Neutral devtools styling (no color themes)
  const botAvatarClass = 'from-muted to-muted/50 border-border/50';
  const botIconClass = 'text-foreground';
  const thinkingAvatarClass = 'from-muted to-muted/50 border-border/50';
  const thinkingPulseClass = 'from-muted-foreground/10 to-muted-foreground/5';
  const thinkingIconClass = 'text-muted-foreground';
  const dotsClass = 'bg-muted-foreground/60';
  const sendBtnClass = 'bg-foreground hover:bg-foreground/90 text-background';
  const promptIconClass = 'text-muted-foreground';
  const emptyIconBg = 'bg-muted';
  const emptyIconGradient = 'from-muted to-muted/50 border-border/50';
  const emptyIconColor = 'text-foreground';

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      if (waitingForFixesTimerRef.current) {
        clearTimeout(waitingForFixesTimerRef.current);
      }
      if (timeoutTimerRef.current) {
        clearTimeout(timeoutTimerRef.current);
      }
    };
  }, []);

  // WebSocket connection management
  useEffect(() => {
    if (!isAuthenticated) return;

    const onConnected = () => {
      setIsConnected(true);
      setConnectionError(null);
    };

    const onDisconnected = () => {
      setIsConnected(false);
      setConnectionError('Chat connection lost. Reconnecting...');
    };

    // Register for immediate connect/disconnect updates
    webSocketClient.onConnect(onConnected);
    webSocketClient.onDisconnect(onDisconnected);

    // Check current state and connect if needed
    if (webSocketClient.isConnected()) {
      setIsConnected(true);
      setConnectionError(null);
    } else {
      webSocketClient.connect();
    }

    // Listen for session ID notifications from the router (same pattern as KubernetesChat)
    // Uses ref to avoid stale closure — the effect only re-runs on [isAuthenticated]
    const handleSessionNotification = (message: any) => {
      if (message.type === 'current_session' && sessionManager.enabled) {
        const sessionId = message.content?.session_id;
        if (sessionId && sessionId !== currentSessionIdRef.current) {
          sessionManager.chatHistory.setCurrentSessionId(sessionId);
          sessionManager.chatHistory.refreshSessions();
        }
      }
    };
    webSocketClient.onMessage(handleSessionNotification);

    // Fallback poll in case callbacks are missed
    const interval = setInterval(() => {
      const connected = webSocketClient.isConnected();
      setIsConnected(connected);
      if (!connected) {
        setConnectionError('Chat connection lost. Reconnecting...');
        webSocketClient.connect();
      } else {
        setConnectionError(null);
      }
    }, 10000);

    return () => {
      clearInterval(interval);
      webSocketClient.offMessage(handleSessionNotification);
      // Remove our callbacks from the arrays to prevent leaks
      const connIdx = (webSocketClient as any).connectCallbacks?.indexOf(onConnected);
      if (connIdx > -1) (webSocketClient as any).connectCallbacks.splice(connIdx, 1);
      const discIdx = (webSocketClient as any).disconnectCallbacks?.indexOf(onDisconnected);
      if (discIdx > -1) (webSocketClient as any).disconnectCallbacks.splice(discIdx, 1);
    };
  }, [isAuthenticated]);

  // Auto-scroll: keep scrolled to bottom during streaming UNLESS user has scrolled up.
  const isNearBottomRef = useRef(true);
  const lastScrollHeightRef = useRef(0);
  const isProgrammaticScrollRef = useRef(false);

  useEffect(() => {
    const el = scrollAreaRef.current;
    if (!el) return;
    const onScroll = () => {
      if (isProgrammaticScrollRef.current) return;
      const dist = el.scrollHeight - el.scrollTop - el.clientHeight;
      isNearBottomRef.current = dist < 80;
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    const el = scrollAreaRef.current;
    if (!el) return;
    const newHeight = el.scrollHeight;
    if (newHeight !== lastScrollHeightRef.current) {
      if (isNearBottomRef.current) {
        isProgrammaticScrollRef.current = true;
        el.scrollTop = newHeight;
        requestAnimationFrame(() => { isProgrammaticScrollRef.current = false; });
      }
      lastScrollHeightRef.current = newHeight;
    }
  }, [messages, currentBotMessage, isThinking, thinkingSteps]);

  const handleSamplePrompt = (prompt: string) => {
    setInput(prompt);
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.focus();
        inputRef.current.style.height = 'auto';
        inputRef.current.style.height = Math.min(inputRef.current.scrollHeight, 120) + 'px';
      }
    }, 50);
  };

  const handleCancel = () => {
    if (currentMessageId) {
      cancelChatMessage(currentMessageId);

      // Remove the response handler to prevent late-arriving messages from showing
      if (responseHandlerRef.current) {
        webSocketClient.offMessage(responseHandlerRef.current);
        responseHandlerRef.current = null;
      }

      const partialText = currentBotMessage;
      const partialThinking = thinkingText;
      const partialSteps = [...thinkingSteps];
      const partialFixes = currentFixes ? [...currentFixes] : null;

      setIsLoading(false);
      setIsTyping(false);
      setIsThinking(false);
      setThinkingComplete(false);
      setThinkingText('');
      setThinkingSteps([]);
      setCurrentBotMessage('');
      setCurrentFixes(null);
      if (waitingForFixesTimerRef.current) clearTimeout(waitingForFixesTimerRef.current);
      if (timeoutTimerRef.current) clearTimeout(timeoutTimerRef.current);
      setWaitingForFixes(false);
      setCanCancel(false);
      setCurrentMessageId(null);

      const fixProp = config.fixType === 'yaml'
        ? { yamlFixes: (partialFixes as YAMLFix[]) || undefined }
        : { cloudFixes: (partialFixes as CloudFix[]) || undefined };

      setMessages((prev) => [...prev, {
        id: `cancelled-${Date.now()}`,
        text: partialText || 'Request cancelled.',
        sender: 'bot',
        timestamp: Date.now(),
        thinking: partialThinking ? { text: partialThinking, steps: partialSteps, isExpanded: false } : undefined,
        ...fixProp,
      }]);
    }
  };

  const handleSendWithInput = async (inputText: string) => {
    if (inputText.trim() === '' || !isConnected || isLoading) return;
    // Prevent double-send from rapid clicks/enter key
    if (sendGuardRef.current) return;
    sendGuardRef.current = true;

    const userMessage: ChatMessage = {
      id: `user-${Date.now()}`,
      text: inputText.trim(),
      sender: 'user',
      timestamp: Date.now(),
      attachments: config.getAttachments?.() || undefined,
    };

    setMessages((prev) => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);
    setIsTyping(false);
    setCurrentBotMessage('');
    setIsThinking(false);
    setThinkingText('');
    setThinkingSteps([]);
    setThinkingComplete(false);
    setIsThinkingExpanded(true);

    if (inputRef.current) {
      inputRef.current.style.height = 'auto';
      inputRef.current.style.height = '44px';
    }

    setTimeout(() => inputRef.current?.focus(), 50);

    let accumulatedMessage = '';
    let accumulatedThinking = '';
    let accumulatedSteps: ThinkingStep[] = [];
    let receivedFixes: YAMLFix[] | CloudFix[] | null = null;

    try {
      // Blank-start chats create their session lazily on the first send, so
      // the router has a session to persist this message into.
      await sessionManager.ensureSession();
      const messageId = config.sendMessage(inputText.trim());
      setCurrentMessageId(messageId);
      setCanCancel(true);

      const botMessageId = `bot-${Date.now()}`;
      let botMessageComplete = false;

      const fixPropForMessage = () => {
        if (!receivedFixes) return {};
        return config.fixType === 'yaml'
          ? { yamlFixes: receivedFixes as YAMLFix[] }
          : { cloudFixes: receivedFixes as CloudFix[] };
      };

      const onFixesReceived = (fixes: YAMLFix[] | CloudFix[]) => {
        receivedFixes = fixes;
        setCurrentFixes(fixes);
        if (waitingForFixesTimerRef.current) clearTimeout(waitingForFixesTimerRef.current);
        setWaitingForFixes(false);
      };

      const onYAMLFixes = config.fixType === 'yaml'
        ? (fixes: Record<string, YAMLFix[]>) => onFixesReceived(Object.values(fixes).flat())
        : undefined;

      const onCloudFixes = config.fixType === 'cloud'
        ? (fixes: Record<string, CloudFix[]>) => onFixesReceived(Object.values(fixes).flat())
        : undefined;

      const handleResponse = handleStreamingResponse(
        messageId,
        // onChunk
        (chunk: string, replace?: boolean) => {
          if (replace) {
            accumulatedMessage = chunk;
          } else {
            accumulatedMessage += chunk;
          }
          setCurrentBotMessage(accumulatedMessage);
          setIsTyping(false);
        },
        // onComplete
        () => {
          if (waitingForFixesTimerRef.current) clearTimeout(waitingForFixesTimerRef.current);
          setWaitingForFixes(false);
          setMessages((prev) => [...prev, {
            id: botMessageId,
            text: accumulatedMessage,
            sender: 'bot',
            timestamp: Date.now(),
            thinking: (accumulatedThinking || accumulatedSteps.length > 0) ? {
              text: accumulatedThinking, steps: accumulatedSteps, isExpanded: false,
            } : undefined,
            ...fixPropForMessage(),
          }]);
          // Builder mode: apply a completed workflow block straight to the
          // host (e.g. the builder canvas) instead of waiting for a button.
          if (config.onWorkflowGenerated) {
            const wfMatch = accumulatedMessage.match(/```workflow\s*\n([\s\S]*?)```/);
            if (wfMatch) {
              try {
                config.onWorkflowGenerated(JSON.parse(wfMatch[1]));
              } catch {
                // Malformed block — leave it rendered in chat only.
              }
            }
          }
          setCurrentBotMessage('');
          setCurrentFixes(null);
          setIsLoading(false);
          setIsTyping(false);
          setIsThinking(false);
          setThinkingComplete(false);
          setThinkingText('');
          setThinkingSteps([]);
          setCanCancel(false);
          setCurrentMessageId(null);
          botMessageComplete = true;
          webSocketClient.offMessage(handleResponse);
        },
        // onThinking
        (thinkingChunk: string, steps?: ThinkingStep[]) => {
          accumulatedThinking += thinkingChunk;
          setThinkingText(accumulatedThinking);
          if (steps && steps.length > 0) {
            const existingStepsMap = new Map(accumulatedSteps.map(s => [s.id, s]));
            let hasChanges = false;
            for (const incomingStep of steps) {
              const existing = existingStepsMap.get(incomingStep.id);
              if (existing) {
                const merged = { ...existing, ...incomingStep };
                if (JSON.stringify(merged) !== JSON.stringify(existing)) {
                  existingStepsMap.set(incomingStep.id, merged);
                  hasChanges = true;
                }
              } else {
                existingStepsMap.set(incomingStep.id, incomingStep);
                hasChanges = true;
              }
            }
            if (hasChanges) {
              accumulatedSteps = Array.from(existingStepsMap.values());
              setThinkingSteps(accumulatedSteps);
            }
          }
          setIsThinking(true);
          setIsTyping(false);
        },
        // onThinkingComplete
        () => {
          setThinkingComplete(true);
          setIsTyping(true);
        },
        // onCancelled
        () => {
          botMessageComplete = true;
          webSocketClient.offMessage(handleResponse);
        },
        // onYAMLFixes
        onYAMLFixes,
        // onError
        (errorMsg: string) => {
          if (botMessageComplete) return; // Already completed, ignore late errors
          botMessageComplete = true;
          webSocketClient.offMessage(handleResponse);
          setIsThinking(false);
          setIsLoading(false);
          setThinkingSteps([]);
          setCanCancel(false);
          setCurrentMessageId(null);
          setCurrentBotMessage('');
          setMessages(prev => [...prev, {
            id: `error-${Date.now()}`,
            text: errorMsg,
            sender: 'bot' as const,
            timestamp: Date.now(),
          }]);
        },
        // onCloudFixes
        onCloudFixes,
        // onTextComplete
        () => {
          const timer = setTimeout(() => setWaitingForFixes(true), 1000);
          waitingForFixesTimerRef.current = timer;
        }
      );

      webSocketClient.onMessage(handleResponse);
      responseHandlerRef.current = handleResponse;

      timeoutTimerRef.current = setTimeout(() => {
        if (!botMessageComplete) {
          botMessageComplete = true;
          webSocketClient.offMessage(handleResponse);
          setMessages((prev) => [...prev, {
            id: botMessageId,
            text: accumulatedMessage || 'Sorry, I encountered an error processing your request. Please try again.',
            sender: 'bot',
            timestamp: Date.now(),
            thinking: (accumulatedThinking || accumulatedSteps.length > 0) ? {
              text: accumulatedThinking, steps: accumulatedSteps, isExpanded: false,
            } : undefined,
            ...fixPropForMessage(),
          }]);
          setCurrentBotMessage('');
          setCurrentFixes(null);
          setIsLoading(false);
          setIsTyping(false);
          setThinkingSteps([]);
          setCanCancel(false);
          setCurrentMessageId(null);
        }
      }, 300000);

    } catch (error) {
      console.error('Failed to send message:', error);
      setMessages((prev) => [...prev, {
        id: `error-${Date.now()}`,
        text: 'Sorry, I encountered an error. Please try again.',
        sender: 'bot',
        timestamp: Date.now(),
      }]);
      setIsLoading(false);
      setIsTyping(false);
      setThinkingSteps([]);
      setCurrentFixes(null);
      setCanCancel(false);
      setCurrentMessageId(null);
    }
  };

  const handleSend = async () => {
    await handleSendWithInput(input);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      if (e.shiftKey) return;
      e.preventDefault();
      handleSend();
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInput(e.target.value);
    const textarea = e.target;
    textarea.style.height = 'auto';
    textarea.style.height = Math.min(textarea.scrollHeight, 120) + 'px';
  };

  // High-contrast Simulated indicator with an explanatory tooltip: black tag
  // with white text in light mode, inverted in dark mode.
  const simulatedBadgeEl = config.simulatedBadge ? (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="inline-flex items-center self-center rounded-md bg-black text-white dark:bg-white dark:text-black px-2.5 py-1 text-xs font-semibold leading-none cursor-help">
            Simulated
          </span>
        </TooltipTrigger>
        <TooltipContent side="bottom" className="max-w-[260px] text-xs">
          Simulated mode: every integration is treated as connected. Workflows you create are sandboxed — test them with mocked outputs now, activate after connecting the integrations they use.
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  ) : null;

  return (
    <div className="relative flex flex-col h-full bg-background">
      {/* Header — hidden in full-page (ChatGPT-style) mode, where the session
          controls render as a slim right-aligned row instead. */}
      {config.hideHeader ? (
        ((sessionManager.enabled && !config.hideSessionControls) || config.renderHeaderExtra || config.simulatedBadge) && (
          messages.length === 0 && !isLoading && !connectionError ? (
            // Empty state: the heading/prompts are vertically centered, so
            // float the controls to sit slightly above that block on desktop
            // (calc top) and at the top-right corner on phones (top-2).
            // When the block overflows the container (compactEmpty), any
            // float overlaps the heading — render the controls as a static
            // row above the content instead (the same layout the
            // active-conversation state uses).
            <div className={cn(
              compactEmpty
                ? "flex items-center justify-end gap-1 px-2 pt-2 flex-shrink-0"
                : "absolute right-2 top-2 z-10 flex items-center gap-1 sm:top-[max(0.5rem,calc(50%-16.5rem))]"
            )}>
              {simulatedBadgeEl}
              {sessionManager.enabled && !config.hideSessionControls && (
                <ChatHistoryDropdown
                  sessions={sessionManager.chatHistory.sessions}
                  currentSessionId={sessionManager.chatHistory.currentSessionId}
                  loading={sessionManager.chatHistory.loading}
                  showHistory={sessionManager.showHistory}
                  setShowHistory={sessionManager.setShowHistory}
                  historyRef={sessionManager.historyRef}
                  onLoadSession={sessionManager.handleLoadSession}
                  onNewChat={sessionManager.handleNewChat}
                  formatSessionDate={sessionManager.formatSessionDate}
                  disabled={isLoading || canCancel}
                />
              )}
              {config.renderHeaderExtra && config.renderHeaderExtra()}
            </div>
          ) : (
            // Active chat: a static row above the messages area so bubbles
            // scroll below the controls instead of underneath them.
            <div className="flex items-center justify-end gap-1 px-2 pt-2 flex-shrink-0">
              {simulatedBadgeEl}
              {sessionManager.enabled && !config.hideSessionControls && (
                <ChatHistoryDropdown
                  sessions={sessionManager.chatHistory.sessions}
                  currentSessionId={sessionManager.chatHistory.currentSessionId}
                  loading={sessionManager.chatHistory.loading}
                  showHistory={sessionManager.showHistory}
                  setShowHistory={sessionManager.setShowHistory}
                  historyRef={sessionManager.historyRef}
                  onLoadSession={sessionManager.handleLoadSession}
                  onNewChat={sessionManager.handleNewChat}
                  formatSessionDate={sessionManager.formatSessionDate}
                  disabled={isLoading || canCancel}
                />
              )}
              {config.renderHeaderExtra && config.renderHeaderExtra()}
            </div>
          )
        )
      ) : (
      <div className="p-4 border-b border-border">
        <div className="flex items-center gap-3">
          {config.headerIcon}
          <div className="flex-1 min-w-0">
            <h3 className="text-lg font-semibold text-foreground">
              {config.headerTitle}
            </h3>
            <div className="flex items-center gap-2 mt-0.5">
              {isConnected ? (
                <>
                  <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                  <span className="text-xs text-green-600 dark:text-green-400 font-medium">Connected</span>
                </>
              ) : (
                <>
                  <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                  <span className="text-xs text-red-600 dark:text-red-400 font-medium">Disconnected</span>
                </>
              )}
              {simulatedBadgeEl}
            </div>
          </div>
          {sessionManager.enabled && !config.hideSessionControls && (
            <ChatHistoryDropdown
              sessions={sessionManager.chatHistory.sessions}
              currentSessionId={sessionManager.chatHistory.currentSessionId}
              loading={sessionManager.chatHistory.loading}
              showHistory={sessionManager.showHistory}
              setShowHistory={sessionManager.setShowHistory}
              historyRef={sessionManager.historyRef}
              onLoadSession={sessionManager.handleLoadSession}
              onNewChat={sessionManager.handleNewChat}
              formatSessionDate={sessionManager.formatSessionDate}
              disabled={isLoading || canCancel}
            />
          )}
          {config.renderHeaderExtra && (
            <div className="flex items-center -ml-2">
              {config.renderHeaderExtra()}
            </div>
          )}
        </div>
      </div>
      )}

      {/* Messages Area */}
      <div ref={scrollAreaRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
        {connectionError && (
          <div className="animate-in slide-in-from-top-2 duration-300">
            <div className="flex items-center gap-3 p-3 bg-red-50/80 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl backdrop-blur-sm">
              <AlertCircle className="h-4 w-4 text-red-500 flex-shrink-0" />
              <p className="text-sm text-red-700 dark:text-red-300 font-medium">{connectionError}</p>
            </div>
          </div>
        )}

        {messages.length === 0 && !isLoading && !connectionError ? (
          <div className={cn(
            "flex flex-col items-center justify-center text-center animate-in fade-in-50 duration-700",
            compactEmpty ? "py-2" : "py-8",
            // Full-page mode (Home Build tab): fill the messages area so the
            // heading/prompts sit vertically centered with the input anchored
            // at the bottom, instead of hugging the top of the page.
            // min-h-full (not h-full): when the block is taller than the
            // container (short phones), h-full + justify-center clips the
            // heading unscrollably; min-h-full lets the container grow so
            // the overflow scrolls normally.
            config.hideHeader && "min-h-full"
          )}>
            {!config.hideEmptyIcon && (
            <div className="relative mb-4">
              <div className={cn("absolute inset-0 rounded-full blur-xl", emptyIconBg)}></div>
              <div className={cn("relative p-4 bg-gradient-to-br rounded-full border", emptyIconGradient)}>
                <MessageCircle className={cn("h-8 w-8", emptyIconColor)} />
              </div>
            </div>
            )}
            <h4 className={cn("font-semibold text-foreground", config.hideHeader ? (compactEmpty ? "text-lg mb-2" : "text-2xl mb-3") : "text-lg mb-2")}>
              {config.emptyHeading}
            </h4>
            <p className={cn("text-muted-foreground leading-relaxed", config.hideHeader ? (compactEmpty ? "text-sm max-w-md mb-4" : "text-base max-w-md mb-6") : "text-sm max-w-sm mb-6")}>
              {config.emptyDescription}
            </p>
            <div className={cn("grid grid-cols-1 w-full", config.hideHeader ? (compactEmpty ? "gap-2 max-w-lg" : "gap-2.5 max-w-lg") : "gap-2 max-w-md")}>
              {config.samplePrompts.map((prompt, index) => (
                <button
                  key={prompt}
                  onClick={() => handleSamplePrompt(prompt)}
                  disabled={!isConnected}
                  className={cn(
                    "bg-card/50 backdrop-blur-sm border border-border/50 rounded-lg text-left",
                    config.hideHeader ? (compactEmpty ? "p-2.5" : "p-3.5") : "p-3",
                    "hover:bg-card/80 hover:border-border hover:shadow-md",
                    "transition-all duration-300 text-sm leading-relaxed",
                    "disabled:opacity-50 disabled:cursor-not-allowed",
                    "animate-in slide-in-from-top-4 duration-500"
                  )}
                  style={{ animationDelay: `${index * 100}ms` }}
                >
                  <div className="flex items-start gap-2">
                    <MessageCircle className={cn("mt-0.5 flex-shrink-0", config.hideHeader ? "h-3.5 w-3.5" : "h-3 w-3", promptIconClass)} />
                    <span className={config.hideHeader ? "text-sm" : "text-xs"}>{prompt}</span>
                  </div>
                </button>
              ))}
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {(() => {
              const completedMessages = isLoading && messages.length > 0 && messages[messages.length - 1].sender === 'user'
                ? messages.slice(0, -1)
                : messages;

              return completedMessages.map((message) => {
                if (message.sender === 'user') {
                  return (
                    <div key={message.id} className="animate-in slide-in-from-bottom-2 duration-500 flex gap-3 justify-end">
                      <div className="max-w-[85%]">
                        {message.attachments && message.attachments.length > 0 && (
                          <div className="flex items-center gap-1.5 justify-end mb-1.5 flex-wrap">
                            {message.attachments.map((att, i) => (
                              <span key={i} className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-md bg-muted text-foreground border border-border font-medium">
                                <AttachmentIcon type={att.type} />
                                <span className="max-w-[160px] truncate">{att.name}</span>
                              </span>
                            ))}
                          </div>
                        )}
                        <div className="rounded-2xl px-3 py-2 shadow-sm bg-foreground text-background">
                          <p className="text-sm leading-relaxed whitespace-pre-wrap">{message.text}</p>
                        </div>
                      </div>
                      <div className="flex-shrink-0 w-7 h-7 rounded-full bg-muted border border-border/50 flex items-center justify-center">
                        <User className="h-3 w-3 text-foreground" />
                      </div>
                    </div>
                  );
                } else {
                  return (
                    <div key={message.id} className="animate-in slide-in-from-bottom-2 duration-500 flex gap-3 justify-start">
                      <div className={cn("flex-shrink-0 w-7 h-7 rounded-full bg-gradient-to-br border flex items-center justify-center", botAvatarClass)}>
                        <Bot className={cn("h-3 w-3", botIconClass)} />
                      </div>
                      <div className="max-w-[85%] space-y-2">
                        {message.thinking && (
                          <ThinkingDisplay thinking={message.thinking} messageId={message.id} />
                        )}
                        <div className="rounded-2xl px-3 py-2 bg-card/80 backdrop-blur-sm border border-border/50 shadow-sm">
                          <div className="text-sm leading-relaxed break-words prose-chat">
                            <ChatMarkdown text={message.text} onWorkflowSave={config.onWorkflowSave} onWorkflowCreate={config.onWorkflowCreate} onWorkflowEdit={config.onWorkflowEdit} compactWorkflowBlocks={config.compactWorkflowBlocks} />
                          </div>
                          {config.renderFixes?.(message)}
                        </div>
                      </div>
                    </div>
                  );
                }
              });
            })()}

            {/* Active conversation — loading indicators */}
            {isLoading && (
              <div className="space-y-3">
                {messages.length > 0 && messages[messages.length - 1].sender === 'user' && (
                  <div className="animate-in slide-in-from-bottom-2 duration-300 flex gap-3 justify-end">
                    <div className="max-w-[85%]">
                      {messages[messages.length - 1].attachments && messages[messages.length - 1].attachments!.length > 0 && (
                        <div className="flex items-center gap-1.5 justify-end mb-1.5 flex-wrap">
                          {messages[messages.length - 1].attachments!.map((att, i) => (
                            <span key={i} className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-md bg-muted text-foreground border border-border font-medium">
                              <AttachmentIcon type={att.type} />
                              <span className="max-w-[160px] truncate">{att.name}</span>
                            </span>
                          ))}
                        </div>
                      )}
                      <div className="rounded-2xl px-3 py-2 shadow-sm bg-foreground text-background">
                        <p className="text-sm leading-relaxed whitespace-pre-wrap">{messages[messages.length - 1].text}</p>
                      </div>
                    </div>
                    <div className="flex-shrink-0 w-7 h-7 rounded-full bg-muted border border-border/50 flex items-center justify-center">
                      <User className="h-3 w-3 text-foreground" />
                    </div>
                  </div>
                )}

                {!isThinking && !isTyping && !currentBotMessage && (
                  <div className="animate-in slide-in-from-bottom-2 duration-300 flex gap-3 justify-start">
                    <div className={cn("flex-shrink-0 w-7 h-7 rounded-full bg-gradient-to-br border flex items-center justify-center", botAvatarClass)}>
                      <Bot className={cn("h-3 w-3", botIconClass)} />
                    </div>
                    <div className="rounded-2xl px-3 py-2">
                      <p className="text-sm font-medium shimmer-text">Thinking</p>
                    </div>
                  </div>
                )}

                {isThinking && (
                  <div className="animate-in slide-in-from-bottom-2 duration-300 flex gap-3 justify-start">
                    <div className={cn("flex-shrink-0 w-7 h-7 rounded-full bg-gradient-to-br border flex items-center justify-center relative overflow-hidden", thinkingAvatarClass)}>
                      <div className={cn("absolute inset-0 bg-gradient-to-r animate-pulse", thinkingPulseClass)} style={{animationDuration: '2s'}}></div>
                      <Brain className={cn("h-3 w-3 relative z-10 animate-pulse", thinkingIconClass)} />
                    </div>
                    <div className="max-w-[85%]">
                      <ThinkingDisplay
                        thinking={{ text: thinkingText, steps: thinkingSteps, isExpanded: isThinkingExpanded }}
                        messageId="streaming"
                        isStreaming={!thinkingComplete}
                      />
                    </div>
                  </div>
                )}

                {isTyping && !currentBotMessage && (
                  <div className="animate-in slide-in-from-bottom-2 duration-300 flex gap-3 justify-start">
                    <div className={cn("flex-shrink-0 w-7 h-7 rounded-full bg-gradient-to-br border flex items-center justify-center", botAvatarClass)}>
                      <Bot className={cn("h-3 w-3", botIconClass)} />
                    </div>
                    <div className="rounded-2xl px-3 py-2 bg-card/80 backdrop-blur-sm border border-border/50 shadow-sm">
                      <div className="flex items-center space-x-1">
                        <span className={cn("h-1.5 w-1.5 rounded-full animate-bounce [animation-delay:-0.3s]", dotsClass)}></span>
                        <span className={cn("h-1.5 w-1.5 rounded-full animate-bounce [animation-delay:-0.15s]", dotsClass)}></span>
                        <span className={cn("h-1.5 w-1.5 rounded-full animate-bounce", dotsClass)}></span>
                      </div>
                    </div>
                  </div>
                )}

                {currentBotMessage && (
                  <div className="animate-in slide-in-from-bottom-2 duration-300 flex gap-3 justify-start">
                    <div className={cn("flex-shrink-0 w-7 h-7 rounded-full bg-gradient-to-br border flex items-center justify-center", botAvatarClass)}>
                      <Bot className={cn("h-3 w-3", botIconClass)} />
                    </div>
                    <div className="max-w-[85%] w-full min-w-0 sm:min-w-[320px] rounded-2xl px-3 py-2 bg-card/80 backdrop-blur-sm border border-border/50 shadow-sm">
                      <div className="text-sm leading-relaxed break-words prose-chat">
                        <ChatMarkdown text={currentBotMessage} onWorkflowSave={config.onWorkflowSave} onWorkflowCreate={config.onWorkflowCreate} onWorkflowEdit={config.onWorkflowEdit} compactWorkflowBlocks={config.compactWorkflowBlocks} />
                      </div>
                      {currentFixes && config.renderStreamingFixes?.(currentFixes)}
                      {waitingForFixes && (
                        <div className="flex items-center gap-2 mt-3 pt-2 border-t border-border/30">
                          <Loader2 className="h-3.5 w-3.5 text-muted-foreground animate-spin" />
                          <span className="text-xs text-muted-foreground">{config.fixesLoadingText || 'Loading fixes'}</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Input Area — no divider in full-page (hideHeader) mode */}
      <div className={cn("p-2", !config.hideHeader && "border-t border-border/50")}>
        {copilotBlocked ? (
          <div className="text-center py-4 px-3">
            <p className="text-sm text-muted-foreground">
              {config.requiredProduct === 'workflows' ? 'Workflows' : 'Cloud AI Copilot'} subscription required to use chat.{' '}
              <Link to="/billing" className="text-foreground underline underline-offset-2 hover:text-foreground/80">
                Subscribe on the Billing page
              </Link>
            </p>
          </div>
        ) : (
        <div className="relative flex flex-col gap-1.5 p-2 border border-border rounded-xl">
          {config.renderInputAbove && config.renderInputAbove()}
          <div className="flex items-end gap-2">
          {config.renderInputPrefix && (
            <div className="flex-shrink-0 self-center">
              {config.renderInputPrefix()}
            </div>
          )}
          <div className="flex-1 min-h-[40px] max-h-24">
            <textarea
              ref={inputRef}
              value={input}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              rows={1}
              placeholder={isConnected ? config.inputPlaceholder : "Connecting..."}
              className={cn(
                "w-full border-0 bg-transparent resize-none leading-relaxed",
                config.hideHeader ? "text-base" : "text-sm",
                "focus-visible:ring-0 focus-visible:ring-offset-0 focus:outline-none placeholder:text-muted-foreground/60",
                "min-h-[40px] max-h-24 py-2 px-3 overflow-y-auto"
              )}
              disabled={isLoading || !isConnected}
            />
          </div>

          <div className="flex-shrink-0 self-center">
            <Button
              onClick={canCancel ? handleCancel : handleSend}
              disabled={(!input.trim() && !canCancel) || !isConnected}
              size="icon"
              className={cn(
                "h-9 w-9 rounded-lg transition-all duration-200",
                "disabled:opacity-50 disabled:cursor-not-allowed",
                canCancel
                  ? "bg-foreground hover:bg-foreground/90 text-background"
                  : sendBtnClass,
                (!input.trim() && !canCancel) || (!isConnected && !canCancel)
                  ? "scale-95 opacity-50"
                  : "scale-100 hover:scale-105 shadow-lg"
              )}
              title={canCancel ? "Stop request" : "Send message"}
            >
              {canCancel ? (
                <X className="h-4 w-4" />
              ) : isLoading ? (
                <div className="h-3 w-3 animate-spin rounded-full border-2 border-primary-foreground border-t-transparent" />
              ) : (
                <Send className="h-3 w-3" />
              )}
            </Button>
          </div>
          </div>
        </div>
        )}

        {!config.hideDisclaimer && (
        <p className="text-xs text-center text-muted-foreground/80 mt-1 px-1">
          AI can make mistakes. Please verify remediation steps before applying to production.
        </p>
        )}
      </div>
    </div>
  );
}
